// AWS WAF JavaScript SDK integration.
//
// Loads the WAF SDK from the configured Application Integration URL
// (set via `window.CC_WIDGET_CONFIG.wafIntegrationUrl`) and exposes
// `getWafToken()` so the API client can add the `X-Aws-Waf-Token`
// header to every middleware call. When the URL is unset (current
// production), this module is fully inert — no script loads, no token
// requested, no header added.
//
// Why we need this:
//   `cc-waf-protection` has CaptchaOnSessionMint + CaptchaOnBookingCreate
//   rules currently in Count mode. Without a token on outbound requests,
//   flipping those rules to Captcha would break the widget — `fetch()`
//   expects JSON but WAF would return a CAPTCHA HTML page. The SDK
//   transparently solves the challenge once on mount, caches the token,
//   and our header injection lets the widget pass the rule.
//
// SDK contract (per AWS docs):
//   The hosted `jsapi.js` defines a global `AwsWafIntegration` with
//   `.getToken(): Promise<string>` and `.hasToken(): boolean`. Token
//   refresh is handled internally — the SDK exposes a single promise
//   for the live token and renews ahead of the WAF immunity window
//   (currently 600s on this ACL).

interface AwsWafIntegrationGlobal {
  getToken(): Promise<string>;
  hasToken(): boolean;
}

declare global {
  interface Window {
    AwsWafIntegration?: AwsWafIntegrationGlobal;
  }
}

let loadStarted = false;
let cachedToken: string | null = null;

function configuredUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const cfg = window.CC_WIDGET_CONFIG ?? {};
  const url = cfg.wafIntegrationUrl;
  if (!url || typeof url !== 'string') return null;
  return url.replace(/\/?$/, '/');
}

/**
 * Loads the AWS WAF SDK script (idempotent) and pre-fetches the first
 * token. Safe to call from widget mount even when no URL is configured —
 * returns silently in that case.
 */
export function initWafToken(): void {
  if (loadStarted) return;
  const base = configuredUrl();
  if (!base) return;
  loadStarted = true;

  const script = document.createElement('script');
  script.src = `${base}jsapi.js`;
  script.async = true;
  script.defer = true;
  script.crossOrigin = 'anonymous';
  script.addEventListener('load', () => {
    void primeToken();
  });
  document.head.appendChild(script);
}

async function primeToken(): Promise<void> {
  if (typeof window === 'undefined') return;
  const sdk = window.AwsWafIntegration;
  if (!sdk) return;
  try {
    cachedToken = await sdk.getToken();
  } catch {
    cachedToken = null;
  }
}

/**
 * Returns the current WAF token if the SDK is loaded and has issued one.
 * Returns null when:
 *   - No `wafIntegrationUrl` configured (production today)
 *   - SDK script hasn't finished loading
 *   - SDK loaded but token request failed
 *
 * Callers should treat null as "no header to add" and proceed normally.
 * The WAF rules are currently in Count mode so missing tokens don't
 * affect routing today — this header just primes the path so flipping
 * to Captcha mode tomorrow is a no-op for legit users.
 */
export function getWafToken(): string | null {
  if (typeof window === 'undefined') return null;
  const sdk = window.AwsWafIntegration;
  if (!sdk || !sdk.hasToken()) return cachedToken;
  // Refresh asynchronously so the next caller benefits. We don't await
  // here because every API call would otherwise pay the round-trip.
  void sdk.getToken().then((t) => {
    cachedToken = t;
  });
  return cachedToken;
}
